import { useState, useRef } from "react";
import { Video, Loader2, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSiteImages, absUrl } from "@/lib/siteConfig";
import { EditableImage } from "@/components/EditableImage";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export function HeroMedia({ slot = "home_hero_video", imageSlot = "home_hero", defaultSrc, alt = "" }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { images, setImage } = useSiteImages();
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const videoUrl = images[slot] ? absUrl(images[slot]) : "";
  const poster = images[imageSlot] ? absUrl(images[imageSlot]) : defaultSrc;

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/settings/image", { slot, url: data.url });
      setImage(slot, data.url);
      toast.success("Video di apertura aggiornato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const removeVideo = async () => {
    try {
      await api.put("/settings/image", { slot, url: "" });
      setImage(slot, "");
      toast.success("Video rimosso");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const adminControls = isAdmin && (
    <div className="absolute top-3 left-3 z-10 flex gap-2 opacity-0 group-hover/hero:opacity-100 transition-opacity">
      <button type="button" data-testid="hero-video-upload-btn" onClick={() => inputRef.current?.click()}
        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full bg-black/70 text-white hover:bg-gold hover:text-[#020617]">
        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Video className="w-3.5 h-3.5" />} {videoUrl ? "Cambia video" : "Carica video"}
      </button>
      {videoUrl && (
        <button type="button" data-testid="hero-video-remove-btn" onClick={removeVideo}
          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full bg-black/70 text-white hover:bg-destructive">
          <Trash2 className="w-3.5 h-3.5" /> Rimuovi
        </button>
      )}
      <input ref={inputRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );

  if (videoUrl) {
    return (
      <div className="relative group/hero rounded-2xl border border-white/10 gold-glow overflow-hidden w-full h-[560px]" data-testid="hero-video">
        <video src={videoUrl} poster={poster} autoPlay muted loop playsInline className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020617]/40 to-transparent pointer-events-none" />
        {adminControls}
      </div>
    );
  }

  return (
    <div className="relative group/hero">
      <EditableImage slot={imageSlot} defaultSrc={defaultSrc} alt={alt} className="rounded-2xl border border-white/10 gold-glow overflow-hidden w-full h-[560px]" imgClassName="w-full h-full object-cover" />
      {adminControls}
    </div>
  );
}
